import React, { useEffect } from "react"
import { Box, Button } from "@mui/material"
import { useLocation, useSearchParams } from "react-router-dom"
import { IFeatureDriftCard, IMetaDataCard } from "../../util/interface"
import { useAppDispatch, useAppSelector } from "../../util/hooks"
import {
  admAsync,
  ccAsync,
  dataDriftAsync,
  emailTriggerAsync,
  featureDriftAsync,
  isqAsync,
  rawDataCSV,
  rawDataQualityAsync,
  rawDataQualityStatusSelector,
  rawProcessedDataCSV,
  resetFullStateDataMetaData,
  schemaDataCSV,
  tabChange,
  updateCardWithEmailTriggerInfo,
} from "./DataMetaDataSlice"
import { MetaDataCard } from "../../components/MetaDataCard"
import {
  ALERT_STATUS_MSGS,
  API_STATUS,
  CSV_FILE_NAME_MAPPER,
  DATA_META_CARDS,
  DOWNLOAD_NOTE,
  FEATURE_DRIFT_CARD,
  META_DATA_KEY_MAPPER,
  SCREENS_DETAILS,
} from "../../util/StaticData"
import { RawDataTable } from "./RawDataTable"
import AdmTable from "./AdmTable"
import CCTable from "./ConsistencyCheck"
import ISQTable from "./ISQTable"
import ISPRTable from "./ISPRTable"
import {
  JsonToCsv,
  downloadCSVFile,
  getCurrentParamsInObj,
} from "../../util/CommonMethods"
import RawDataDetailsTable from "./RawDataDetailsTable"
import {
  marketSinleSelectedValueSelector,
  pipeLineNamesAndRunIdsAsync,
  pipeLineSelectedValueSelector,
  runIdChange,
  updateStartAndEndDateBasedOnRunId,
  useCaseSelectedValueSelector,
} from "../Monitor/FilterSlice"
import { Note } from "../../components/Note"
import { setAlertData, setAlertData2 } from "../Monitor/MonitorSlice"
import DownloadButton from "./DownloadBtn"
import DataDriftCheckTable from "./DataDriftCheckTable"
import { FeatureDriftCard } from "../../components/FeatureDriftCard"
import FeatureDriftTable from "./FeatureDriftTable"

const DataMetaData = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  // Selectors to pull state from the Redux store
  const {
    tabs,
    activeTab,
    selectedRawDataRow,
    ccRawData: ccData,
    isprRawData: isprData,
    isqRawData: isqData,
    admRawData: admData,
    isRdqDetailsTableActive,
    statusMessages,
    dataDriftData,
    featureDriftData,
    admStatus,
    ccStatus,
    isqStatus,
    dataDriftStatus,
    featureDriftStatus,
  }: any = useAppSelector((state) => state.dataMetaData)

  // Selector for the selected run ID
  const selectedRunIdFromMonitoring = useAppSelector(
    (store) =>
      store.monitor.pipelineRunDialog?.selectedRunDetails?.az_pipeline_run_id,
  )
  const pipeLineSelectedValue = useAppSelector(pipeLineSelectedValueSelector)
  const runIdSelectedValue = useAppSelector(
    (state) => state.filters.runIdSelectedValue,
  )

  const dispatch = useAppDispatch()
  const rawDataQualityStatus = useAppSelector(rawDataQualityStatusSelector)
  const rawDataInfoTableStatus = useAppSelector(
    (store) => store.dataMetaData.rawDataInfoTableStatus,
  )

  // Effect to handle alert messages based on status
  useEffect(() => {
    if (
      statusMessages[activeTab] &&
      (rawDataQualityStatus === API_STATUS.FAILED ||
        rawDataInfoTableStatus === API_STATUS.FAILED ||
        admStatus === API_STATUS.FAILED ||
        ccStatus === API_STATUS.FAILED ||
        isqStatus === API_STATUS.FAILED ||
        dataDriftStatus === API_STATUS.FAILED ||
        featureDriftStatus === API_STATUS.FAILED)
    ) {
      let tabName = DATA_META_CARDS.find(
        (obj: IMetaDataCard) => obj.key === activeTab,
      )?.title

      if (!tabName) {
        tabName = FEATURE_DRIFT_CARD.title
      }
      dispatch(
        setAlertData2(
          setAlertData(
            (statusMessages[activeTab] as string) + " " + tabName,
            ALERT_STATUS_MSGS.ERROR,
            true,
          ),
        ),
      )
    }
  }, [
    activeTab,
    rawDataQualityStatus,
    admStatus,
    rawDataInfoTableStatus,
    ccStatus,
    isqStatus,
    dataDriftStatus,
    featureDriftStatus,
  ])

  const { pathname } = useLocation()

  // Scroll to the top on location change
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  // Update start and end dates based on run ID
  useEffect(() => {
    const ref: any = searchParams.get("ref")
    if (!ref && !startDate && !endDate) {
      dispatch(updateStartAndEndDateBasedOnRunId())
    }
  }, [runIdSelectedValue, pipeLineSelectedValue])

  // Update search params with the selected run ID
  useEffect(() => {
    if (selectedRunIdFromMonitoring) {
      const tempParams: any = getCurrentParamsInObj(searchParams)
      setSearchParams({
        ...tempParams,
        runId: selectedRunIdFromMonitoring,
        ref: SCREENS_DETAILS.MONITORING,
      })
    }
  }, [selectedRunIdFromMonitoring])

  // Download handler for exporting data as CSV
  const downloadHandler = async (activeTab: string, actionType?: string) => {
    let csv: any
    let fileName = CSV_FILE_NAME_MAPPER[activeTab]

    // Determine the CSV data based on the active tab
    switch (activeTab) {
      case META_DATA_KEY_MAPPER.AMC:
        csv = JsonToCsv(admData)
        break
      case META_DATA_KEY_MAPPER.CC:
        csv = JsonToCsv(ccData)
        break
      case META_DATA_KEY_MAPPER.ISPR:
        csv = JsonToCsv(isprData)
        break
      case META_DATA_KEY_MAPPER.ISQC:
        csv = JsonToCsv(isqData)
        break
      case META_DATA_KEY_MAPPER.DDC:
        csv = JsonToCsv(dataDriftData)
        break
      case META_DATA_KEY_MAPPER.FD:
        csv = JsonToCsv(featureDriftData)
        break
      case META_DATA_KEY_MAPPER.RDQ: {
        let response: any
        // Handle different action types for RDQ
        if (actionType && actionType === "schema") {
          fileName += "_" + actionType + "_" + selectedRawDataRow
          response = await dispatch(schemaDataCSV())
        }
        if (actionType && actionType === "unique") {
          fileName += "_" + actionType + " and not null"
          response = await dispatch(rawDataCSV())
        }
        if (actionType && actionType === "raw") {
          fileName += "_" + actionType + "_" + selectedRawDataRow
          response = await dispatch(rawProcessedDataCSV())
        }
        csv = response.payload
        break
      }
    }
    // Trigger the download of the CSV file
    downloadCSVFile(csv, fileName)
  }

  // Reset state when the component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetFullStateDataMetaData())
    }
  }, [])

  // Sync search params with state when location changes
  useEffect(() => {
    const tab: any = searchParams.get("tab")
    const runId: any = searchParams.get("runId")
    if (runId) {
      dispatch(runIdChange(runId))
    }
    dispatch(tabChange(tab))
  }, [location])

  const startDate = useAppSelector((state) => state.filters.startDate)
  const endDate = useAppSelector((state) => state.filters.endDate)

  // Update start and end dates based on search params
  useEffect(() => {
    const runId: any = searchParams.get("runId")
    const ref: any = searchParams.get("ref")
    if (
      runId &&
      pipeLineSelectedValue &&
      runIdSelectedValue &&
      ref === "monitor"
    ) {
      if (!startDate && !endDate) {
        dispatch(updateStartAndEndDateBasedOnRunId())
      }
    }
  }, [location, pipeLineSelectedValue, runIdSelectedValue])

  // Handle tab clicks
  const tabClick = (obj: IMetaDataCard | IFeatureDriftCard) => {
    dispatch(tabChange(obj.key))
    const tempParams: any = getCurrentParamsInObj(searchParams)
    const params = {
      ...tempParams,
      runId: searchParams.get("runId"),
      tab: obj.key,
    }
    if (!params.runId) {
      delete params.runId // Remove runId if not set
    }
    setSearchParams(params)
  }

  const featureDriftCardData = useAppSelector(
    (store) => store.dataMetaData.featureDriftCardData,
  )
  const useCaseSelectedValue = useAppSelector(useCaseSelectedValueSelector)
  const marketSingleSelectedValue = useAppSelector(
    marketSinleSelectedValueSelector,
  )

  // Fetch data based on selected values
  useEffect(() => {
    if (
      runIdSelectedValue &&
      useCaseSelectedValue &&
      marketSingleSelectedValue
    ) {
      dispatch(admAsync())
      dispatch(ccAsync())
      dispatch(isqAsync())
      dispatch(rawDataQualityAsync())
      dispatch(dataDriftAsync())
      dispatch(featureDriftAsync())

      // Trigger email notifications for data export
      DATA_META_CARDS.map(async (obj: IMetaDataCard) => {
        try {
          const response: any = await dispatch(
            emailTriggerAsync({ file_type: obj.file_type || "" }),
          )
          const emailTrigger =
            response.payload?.["data"]?.[0]?.["email_triggered"]
          if (
            response.payload["data"] &&
            emailTrigger &&
            response.payload?.["data"]?.[0]["f_notification"]["emailed_to"]
          ) {
            const emailText =
              response.payload?.["data"]?.[0]["f_notification"]["emailed_to"]
            dispatch(
              updateCardWithEmailTriggerInfo({
                emailText,
                emailTrigger,
                key: obj.key,
              }),
            )
          } else {
            dispatch(
              updateCardWithEmailTriggerInfo({
                emailText: "",
                emailTrigger: false,
                key: obj.key,
              }),
            )
          }
        } catch (error) {
          console.error(error)
        }
      })
    }
  }, [
    runIdSelectedValue,
    useCaseSelectedValue,
    marketSingleSelectedValue,
    dispatch,
  ])

  // Fetch pipeline names and run IDs based on selected use case and market
  useEffect(() => {
    if (useCaseSelectedValue && marketSingleSelectedValue) {
      dispatch(pipeLineNamesAndRunIdsAsync())
    }
  }, [useCaseSelectedValue, marketSingleSelectedValue])

  return (
    <Box sx={{ display: "flex" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {/* Mapping over the tabs to render MetaDataCard components */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flex: 1,
            gap: 1,
          }}
        >
          {tabs.map((obj: IMetaDataCard) => {
            return (
              <Box
                onClick={() => tabClick(obj)}
                key={obj.title}
                sx={{ flex: 1 }}
              >
                <MetaDataCard activeTab={activeTab} cardDetails={obj} />
              </Box>
            )
          })}

          {/* Special case for the Feature Drift Card */}
          <Box
            onClick={() => tabClick(FEATURE_DRIFT_CARD)}
            key={FEATURE_DRIFT_CARD.title}
            sx={{ flex: 1 }}
          >
            <FeatureDriftCard
              activeTab={activeTab}
              cardDetails={featureDriftCardData}
            />
          </Box>
        </Box>

        {/* Bottom section for notes and download button */}
        <div className="grid grid-cols-1 w-full">
          <Box sx={{ width: "100%" }}>
            {activeTab !== META_DATA_KEY_MAPPER.RDQ ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "end",
                  padding: "5px 20px 5px 5px",
                }}
              >
                {/* Note for AMC mapping criteria */}
                <Note
                  note={
                    activeTab === META_DATA_KEY_MAPPER.AMC
                      ? "Note : Accept criteria for ADM mapping check is 100%"
                      : ""
                  }
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    alignItems: "flex-end",
                  }}
                >
                  {/* Download note based on active tab */}
                  <Note
                    note={
                      (DOWNLOAD_NOTE as { [key: string]: string })[activeTab]
                    }
                  />
                  <Button
                    variant="contained"
                    onClick={() => {
                      downloadHandler(activeTab)
                    }}
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": {
                        boxShadow: "none", // Disable box shadow (elevation) on hover
                      },
                    }}
                  >
                    Download
                  </Button>
                </Box>
              </Box>
            ) : (
              // Conditional rendering for RDQ tab
              <Box
                sx={{
                  display: "flex",
                  padding: "5px 20px 5px 5px",
                  justifyContent: "space-between",
                  gap: 2,
                  alignItems: "flex-end",
                }}
              >
                <Note
                  note={
                    activeTab === META_DATA_KEY_MAPPER.RDQ
                      ? "Note : Select a dataset to download Unique or Raw Processed report"
                      : ""
                  }
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    alignItems: "flex-end",
                  }}
                >
                  <Note
                    note={
                      (DOWNLOAD_NOTE as { [key: string]: string })[activeTab]
                    }
                  />
                  {/* Custom download button for RDQ */}
                  <DownloadButton
                    activeTab={activeTab}
                    selectedRow={selectedRawDataRow}
                    downloadHandler={downloadHandler}
                  />
                </Box>
              </Box>
            )}

            <Box sx={{ width: "100%" }}>
              {/* Conditional rendering for different tables based on active tab */}
              {activeTab == META_DATA_KEY_MAPPER.RDQ &&
              !isRdqDetailsTableActive ? (
                <RawDataTable />
              ) : (
                ""
              )}
              {activeTab == META_DATA_KEY_MAPPER.RDQ &&
              isRdqDetailsTableActive ? (
                <RawDataDetailsTable />
              ) : (
                ""
              )}
              {activeTab == META_DATA_KEY_MAPPER.AMC ? <AdmTable /> : ""}
              {activeTab == META_DATA_KEY_MAPPER.CC ? <CCTable /> : ""}
              {activeTab == META_DATA_KEY_MAPPER.ISPR ? <ISPRTable /> : ""}
              {activeTab == META_DATA_KEY_MAPPER.ISQC ? <ISQTable /> : ""}
              {activeTab == META_DATA_KEY_MAPPER.DDC ? (
                <DataDriftCheckTable />
              ) : (
                ""
              )}
              {activeTab == META_DATA_KEY_MAPPER.FD ? (
                <FeatureDriftTable />
              ) : (
                ""
              )}
            </Box>
          </Box>
        </div>
      </Box>
    </Box>
  )
}

export default DataMetaData
